import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { BrowseResult, DriveFile } from "@/types/api";

export interface LatticeProject {
  id: string;
  customer_id: string;
  name: string;
  slug: string;
  description: string;
  instructions: string;
  agent_template: string;
  assignment_mode: "all" | "selected";
  assigned_agents: string[];
  drive_root_kind: "mine" | "shared";
  drive_folder_id: string;
  drive_folder_name: string;
  drive_web_url: string | null;
  created_at: number;
  updated_at: number;
  /** "project": one kind since 25 Oct 2026 (a workspace not migrated yet may
   *  still answer the older person-project kind; the dashboard shows both alike). */
  kind: string;
}

export interface ProjectUploadFailure {
  filename: string;
  error: string;
  message?: string;
  status?: number;
  upload_id?: string;
}

export interface ProjectUploadResponse {
  project_id: string;
  project_name: string;
  uploaded: DriveFile[];
  promoted_upload_ids?: string[];
  failures: ProjectUploadFailure[];
}

export function useProjects(customerId: string | null) {
  return useQuery({
    queryKey: ["projects", customerId],
    enabled: !!customerId,
    queryFn: ({ signal }) =>
      apiFetch<{ projects: LatticeProject[] }>(
        `/api/customers/${customerId}/projects`,
        { signal },
      ),
    staleTime: 5_000,
    // Agents create projects from chat (a gather makes its own folder):
    // pick them up without a manual refresh.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      name,
      description,
      agentTemplate,
      assignmentMode,
      assignedAgents,
      instructions,
    }: {
      customerId: string;
      name: string;
      description?: string;
      instructions?: string;
      agentTemplate?: string;
      assignmentMode?: "all" | "selected";
      assignedAgents?: string[];
    }) =>
      apiFetch<{ project: LatticeProject }>(
        `/api/customers/${customerId}/projects`,
        {
          method: "POST",
          body: {
            name,
            description: description ?? "",
            instructions: instructions ?? "",
            agent_template: agentTemplate || null,
            assignment_mode: assignmentMode ?? "selected",
            assigned_agents: assignedAgents ?? [],
          },
        },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["projects", vars.customerId] });
      // the server takes a deleted project out of its folder
      void qc.invalidateQueries({ queryKey: ["project-groups", vars.customerId] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      projectId,
      description,
      instructions,
      agentTemplate,
      assignmentMode,
      assignedAgents,
    }: {
      customerId: string;
      projectId: string;
      description?: string;
      instructions?: string;
      agentTemplate?: string;
      assignmentMode?: "all" | "selected";
      assignedAgents?: string[];
    }) =>
      apiFetch<{ project: LatticeProject }>(
        `/api/customers/${customerId}/projects/${projectId}`,
        {
          method: "PATCH",
          body: {
            ...(description !== undefined ? { description } : {}),
            ...(instructions !== undefined ? { instructions } : {}),
            ...(agentTemplate !== undefined ? { agent_template: agentTemplate } : {}),
            ...(assignmentMode !== undefined ? { assignment_mode: assignmentMode } : {}),
            ...(assignedAgents !== undefined ? { assigned_agents: assignedAgents } : {}),
          },
        },
      ),
    onSuccess: (result, vars) => {
      qc.setQueryData<{ projects: LatticeProject[] }>(
        ["projects", vars.customerId],
        (current) => {
          if (!current) return current;
          return {
            projects: current.projects.map((project) =>
              project.id === result.project.id ? result.project : project,
            ),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: ["projects", vars.customerId] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      projectId,
    }: {
      customerId: string;
      projectId: string;
    }) =>
      apiFetch<{
        project_id: string;
        drive_folder_id: string;
        trashed_drive_folder: boolean;
      }>(`/api/customers/${customerId}/projects/${projectId}`, {
        method: "DELETE",
      }),
    onSuccess: (result, vars) => {
      qc.setQueryData<{ projects: LatticeProject[] }>(
        ["projects", vars.customerId],
        (current) => {
          if (!current) return current;
          return {
            projects: current.projects.filter(
              (project) => project.id !== result.project_id,
            ),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: ["projects", vars.customerId] });
    },
  });
}

export function useProjectFiles(scope: {
  customerId: string;
  projectId: string;
  folderId?: string | null;
} | null) {
  return useQuery({
    queryKey: [
      "project-files",
      scope?.customerId,
      scope?.projectId,
      scope?.folderId ?? null,
    ],
    enabled: scope !== null,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (scope!.folderId) params.set("folder", scope!.folderId);
      const suffix = params.toString() ? `?${params}` : "";
      return apiFetch<BrowseResult>(
        `/api/customers/${scope!.customerId}/projects/${scope!.projectId}/files${suffix}`,
        { signal },
      );
    },
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
}

// Uploads moved to api/projectUploads.ts (chunked sessions, progress,
// resume). What stays here is what the project page still reads or mutates.

export function usePromoteStagedProjectFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      projectId,
      uploadIds,
    }: {
      customerId: string;
      projectId: string;
      uploadIds: string[];
    }) =>
      apiFetch<ProjectUploadResponse>(
        `/api/customers/${customerId}/projects/${projectId}/files/staged`,
        {
          method: "POST",
          body: { upload_ids: uploadIds },
        },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: ["project-files", vars.customerId, vars.projectId],
      });
    },
  });
}
